import { parse as parseQueryString, stringify } from 'querystring';

import type { CurrentNetworkContext, Group, GroupFeedParameters } from '@ms/yammer-data/dist/state/types';

import type { GetBaseUrlProps } from '../location/getBaseUrl';
import { getBaseUrl } from '../location/getBaseUrl';

import type { GroupAgentSuggestionsFeedType } from './agentSuggestionsTypes';

type GroupPage = (groupId: Group['id']) => string;

const groupIdParam = ':groupId';
const replaceGroupId = (path: string, groupId: Group['id']) => path.replace(groupIdParam, groupId);

export const groupsRootPath = '/groups';
export const groupParentSubpath = '/:groupId';
export const groupParentPath: `${typeof groupsRootPath}${typeof groupParentSubpath}` = `${groupsRootPath}${groupParentSubpath}`;
export const getGroupParentPath: GroupPage = (groupId) => replaceGroupId(groupParentPath, groupId);
/**
 * @deprecated Do not link to a generic page which performs logic and redirects again, this is an anti-pattern
 * which increases the user-experienced latency from click to eventually landing on the desired page. Instead,
 * perform any necessary logic when generating the url so you link directly to the desired page.
 *
 * Instead of `groupFeedRedirectPage` link directly to `groupFeedAllPage`, or `groupFeedNewPage` if there are
 * unseen messages. You can use the `useGroupFeedAllOrNewLinkPath` hook to handle that logic for you.
 */
export const groupFeedRedirectPage: GroupPage = (groupId) => replaceGroupId(groupParentPath, groupId);
export const groupFeedNewSubpath = '/new';
export const groupFeedNewPath: `${typeof groupParentPath}${typeof groupFeedNewSubpath}` = `${groupParentPath}${groupFeedNewSubpath}`;
export const groupFeedNewPage: GroupPage = (groupId) => replaceGroupId(groupFeedNewPath, groupId);
export const groupFeedNewPostsSubpath = '/new-posts';
export const groupFeedNewPostsPath: `${typeof groupParentPath}${typeof groupFeedNewPostsSubpath}` = `${groupParentPath}${groupFeedNewPostsSubpath}`;
export const groupFeedNewPostsPage: GroupPage = (groupId) => replaceGroupId(groupFeedNewPostsPath, groupId);
export const groupFeedAllSubpath = '/all';
export const groupFeedAllPath: `${typeof groupParentPath}${typeof groupFeedAllSubpath}` = `${groupParentPath}${groupFeedAllSubpath}`;
export const groupFeedAllPage: GroupPage = (groupId) => replaceGroupId(groupFeedAllPath, groupId);
export const groupFeedGenericQueryParamsSubpath = '/feed';
export const groupFeedGenericQueryParamsPath: `${typeof groupParentPath}${typeof groupFeedGenericQueryParamsSubpath}` = `${groupParentPath}${groupFeedGenericQueryParamsSubpath}`;
export const groupFeedGenericQueryParamsPage: GroupPage = (groupId) =>
  replaceGroupId(groupFeedGenericQueryParamsPath, groupId);
export const groupFeedAllOrGenericFeedSubpath = '/(all|feed)';
export const groupFeedQuestionsSubpath = '/questions';
export const groupFeedQuestionsPath: `${typeof groupParentPath}${typeof groupFeedQuestionsSubpath}` = `${groupParentPath}${groupFeedQuestionsSubpath}`;
export const groupFeedQuestionsPage: GroupPage = (groupId) => replaceGroupId(groupFeedQuestionsPath, groupId);
export const groupFeedUnansweredQuestionsSubpath = '/unanswered';
export const groupFeedUnansweredQuestionsPath: `${typeof groupParentPath}${typeof groupFeedUnansweredQuestionsSubpath}` = `${groupParentPath}${groupFeedUnansweredQuestionsSubpath}`;
export const groupFeedUnansweredQuestionsPage: GroupPage = (groupId) =>
  replaceGroupId(groupFeedUnansweredQuestionsPath, groupId);
export const groupFeedNoRepliesQuestionsSubpath = '/norepliesquestions';
export const groupFeedNoRepliesQuestionsPath: `${typeof groupParentPath}${typeof groupFeedNoRepliesQuestionsSubpath}` = `${groupParentPath}${groupFeedNoRepliesQuestionsSubpath}`;
export const groupFeedNoRepliesQuestionsPage: GroupPage = (groupId) =>
  replaceGroupId(groupFeedNoRepliesQuestionsPath, groupId);
export const groupAboutSubpath = '/about';
export const groupAboutPath: `${typeof groupParentPath}${typeof groupAboutSubpath}` = `${groupParentPath}${groupAboutSubpath}`;
export const groupAboutPage: GroupPage = (groupId) => replaceGroupId(groupAboutPath, groupId);
export const groupRelatedCommunitiesSubpath = '/related-communities';
export const groupRelatedCommunitiesPath: `${typeof groupParentPath}${typeof groupRelatedCommunitiesSubpath}` = `${groupParentPath}${groupRelatedCommunitiesSubpath}`;
export const groupRelatedCommunitiesPage: GroupPage = (groupId) => replaceGroupId(groupRelatedCommunitiesPath, groupId);
export const groupEventsSubpath = '/events';
export const groupEventsLiveSubpath: string = `${groupEventsSubpath}/live`;
export const groupEventsPastSubpath: string = `${groupEventsSubpath}/past`;
export const groupEventsUpcomingSubpath: string = `${groupEventsSubpath}/upcoming`;
export const groupEventsPath: `${typeof groupParentPath}${typeof groupEventsSubpath}` = `${groupParentPath}${groupEventsSubpath}`;
export const groupEventsPage: GroupPage = (groupId) => replaceGroupId(groupEventsPath, groupId);
export const groupCampaignsSubpath = '/campaigns';
export const groupCampaignsPath: `${typeof groupParentPath}${typeof groupCampaignsSubpath}` = `${groupParentPath}${groupCampaignsSubpath}`;
export const groupCampaignsPage: GroupPage = (groupId) => replaceGroupId(groupCampaignsPath, groupId);
export const groupSettingsSubpath = '/settings';
export const groupSettingsPath: `${typeof groupParentPath}${typeof groupSettingsSubpath}` = `${groupParentPath}${groupSettingsSubpath}`;
type GroupSettingsPage = (groupId: Group['id'], queryParams?: Record<'section', string>) => string;
export const groupSettingsPage: GroupSettingsPage = (groupId, queryParams) => {
  const basePath = replaceGroupId(groupSettingsPath, groupId);

  return queryParams ? `${basePath}?${stringify(queryParams)}` : basePath;
};
export const groupDeleteSubpath = '/delete';
export const groupDeletePath: `${typeof groupParentPath}${typeof groupDeleteSubpath}` = `${groupParentPath}${groupDeleteSubpath}`;
export const groupDeletePage: GroupPage = (groupId) => replaceGroupId(groupDeletePath, groupId);
export const groupInsightsSubpath = '/activity/overview';
export const groupInsightsPath: `${typeof groupParentPath}${typeof groupInsightsSubpath}` = `${groupParentPath}${groupInsightsSubpath}`;
export const groupInsightsPage: GroupPage = (groupId) => replaceGroupId(groupInsightsPath, groupId);
export const groupQnAInsightsSubpath = '/activity/qna';
export const groupQnAInsightsPath: `${typeof groupParentPath}${typeof groupQnAInsightsSubpath}` = `${groupParentPath}${groupQnAInsightsSubpath}`;
export const groupQnAInsightsPage: GroupPage = (groupId) => replaceGroupId(groupQnAInsightsPath, groupId);
export const groupPinnedLinksSubpath = '/pinned-links';
export const groupPinnedLinksPath: `${typeof groupParentPath}${typeof groupPinnedLinksSubpath}` = `${groupParentPath}${groupPinnedLinksSubpath}`;
export const groupPinnedLinksPage: GroupPage = (groupId) => replaceGroupId(groupPinnedLinksPath, groupId);
export const groupFilesSubpath = '/files';
export const groupFilesPath: `${typeof groupParentPath}${typeof groupFilesSubpath}` = `${groupParentPath}${groupFilesSubpath}`;
export const groupFilesPage: GroupPage = (groupId) => replaceGroupId(groupFilesPath, groupId);
export const groupInfoSubpath = '/info';
export const groupInfoPath: `${typeof groupParentPath}${typeof groupInfoSubpath}` = `${groupParentPath}${groupInfoSubpath}`;
export const groupInfoPage: GroupPage = (groupId) => replaceGroupId(groupInfoPath, groupId);
export const groupSuggestedContentSubpath = '/m365-copilot-suggested-content';
export const groupSuggestedContentPath: `${typeof groupParentPath}${typeof groupSuggestedContentSubpath}` = `${groupParentPath}${groupSuggestedContentSubpath}`;
export const groupSuggestedContentPage: GroupPage = (groupId) => replaceGroupId(groupSuggestedContentPath, groupId);
export const groupTopQuestionsSubpath = '/top-questions';
export const groupTopQuestionsPath: `${typeof groupParentPath}${typeof groupTopQuestionsSubpath}` = `${groupParentPath}${groupTopQuestionsSubpath}`;
export const groupTopQuestionsPage: GroupPage = (groupId) => replaceGroupId(groupTopQuestionsPath, groupId);
export const groupSimilarQuestionsSubpath = '/similar-questions';
export const groupSimilarQuestionsPath: `${typeof groupParentPath}${typeof groupSimilarQuestionsSubpath}` = `${groupParentPath}${groupSimilarQuestionsSubpath}`;
export const groupSimilarQuestionsPage: GroupPage = (groupId) => replaceGroupId(groupSimilarQuestionsPath, groupId);
export const groupAgentSettingsSubpath = '/agent/settings';
export const groupAgentSettingsPath: `${typeof groupParentPath}${typeof groupAgentSettingsSubpath}` = `${groupParentPath}${groupAgentSettingsSubpath}`;
export const groupAgentSettingsPage: GroupPage = (groupId) => replaceGroupId(groupAgentSettingsPath, groupId);
export const groupAgentSuggestionsSubpath = '/agent/suggestions';
export const groupAgentSuggestionsPath: `${typeof groupParentPath}${typeof groupAgentSuggestionsSubpath}` = `${groupParentPath}${groupAgentSuggestionsSubpath}`;
export const groupAgentSuggestionsPage: GroupPage = (groupId) => replaceGroupId(groupAgentSuggestionsPath, groupId);

interface AgentSuggestionsPageParameters {
  readonly filterBy: GroupAgentSuggestionsFeedType;
}
type GroupAgentSuggestionsPageWithParameters = (
  groupId: Group['id'],
  options: AgentSuggestionsPageParameters
) => string;
export const groupAgentSuggestionsPageWithParameters: GroupAgentSuggestionsPageWithParameters = (
  groupId,
  { filterBy }
) => {
  const params = { filterBy: filterBy.toLowerCase() };

  return `${groupAgentSuggestionsPage(groupId)}?${stringify(params)}`;
};
export const groupMembersSubPath = '/members';
export const groupMembersPath: `${typeof groupParentPath}${typeof groupMembersSubPath}` = `${groupParentPath}${groupMembersSubPath}`;
export const groupMembersPage: GroupPage = (groupId) => replaceGroupId(groupMembersPath, groupId);
export const groupMemberRequestsSubPath = '/requests';
export const groupMemberRequestsPath: `${typeof groupParentPath}${typeof groupMembersSubPath}${typeof groupMemberRequestsSubPath}` = `${groupParentPath}${groupMembersSubPath}${groupMemberRequestsSubPath}`;
export const groupMemberRequestsPage: GroupPage = (groupId) => replaceGroupId(groupMemberRequestsPath, groupId);
export const groupMembersAutoEnrollmentSubPath = '/criteria';
export const groupMembersAutoEnrollmentPath: `${typeof groupParentPath}${typeof groupMembersSubPath}${typeof groupMembersAutoEnrollmentSubPath}` = `${groupParentPath}${groupMembersSubPath}${groupMembersAutoEnrollmentSubPath}`;
export const groupMembersAutoEnrollmentPage: GroupPage = (groupId) =>
  replaceGroupId(groupMembersAutoEnrollmentPath, groupId);
export const groupSubpagePaths: string[] = [
  groupSettingsPath,
  groupMembersPath,
  groupMemberRequestsPath,
  groupAgentSuggestionsPath,
  groupAgentSettingsPath,
];
export const groupMembershipApprovalPage = '/groups/memberships/pending';
export const groupNewKey = 'new';
export const groupTypeKey = 'postType';
export const groupSortByKey = 'sortBy';
interface GroupPageUrlOptions {
  readonly groupId: Group['id'];
  readonly host?: GetBaseUrlProps['host'];
  readonly currentNetworkContext?: CurrentNetworkContext;
}
type GroupFeedRedirectPageUrl = (options: GroupPageUrlOptions) => string;
/**
 * @deprecated Do not link to a generic page which performs logic and redirects again, this is an anti-pattern
 * which increases the user-experienced latency from click to eventually landing on the desired page. Instead,
 * perform any necessary logic when generating the url so you link directly to the desired page.
 *
 * Instead of `groupFeedRedirectPageUrl` link directly to `groupFeedAllPage`, or `groupFeedNewPage` if there are
 * unseen messages. You can use the `useGroupFeedAllOrNewLinkPath` hook to handle that logic for you.
 */
export const groupFeedRedirectPageUrl: GroupFeedRedirectPageUrl = ({ groupId, host, currentNetworkContext }) => {
  const baseUrl = getBaseUrl({ host, currentNetworkContext });

  return `${baseUrl}${groupFeedRedirectPage(groupId)}`;
};
type ConvertGroupDatabaseIdToUrlId = (groupDatabaseId: Group['databaseId']) => Group['databaseId'];
export const convertGroupDatabaseIdToUrlId: ConvertGroupDatabaseIdToUrlId = (groupDatabaseId) =>
  groupDatabaseId === '0' ? 'company' : groupDatabaseId;
type GroupFeedPageWithParameters = (groupId: Group['id'], groupParameters: GroupFeedParameters) => string;
export const groupFeedPageWithParameters: GroupFeedPageWithParameters = (groupId, groupParameters) =>
  `${groupFeedGenericQueryParamsPage(groupId)}${makeQueryStringWithGroupFeedParameters(groupParameters)}`;
type MakeQueryStringWithGroupFeedParameters = (groupFeedParameters: GroupFeedParameters) => string;
export const makeQueryStringWithGroupFeedParameters: MakeQueryStringWithGroupFeedParameters = ({
  new: isNew,
  postType,
  sortBy,
}) => {
  const isNewParameter = isNew ? { [groupNewKey]: isNew } : { [groupNewKey]: false };
  const postTypeParameter = postType ? { [groupTypeKey]: postType } : { [groupTypeKey]: 'ALL' };
  const sortByParameter = sortBy ? { [groupSortByKey]: sortBy } : { [groupSortByKey]: 'UPDATED_AT' };
  const queryParameters = { ...isNewParameter, ...postTypeParameter, ...sortByParameter };

  return `?${stringify(queryParameters)}`;
};
type GroupSuggestedArticlePage = (props: { readonly groupId: Group['id']; readonly articleName: string }) => string;
export const groupSuggestedArticlePage: GroupSuggestedArticlePage = ({ groupId, articleName }) => {
  const queryString = window.location.search.substring(1);
  const parsedQuery = parseQueryString(queryString);

  return `/groups/${groupId}/suggested-article?${stringify({ ...parsedQuery, articleName })}`;
};
